import { makeObservable, observable } from "mobx";
import type { AddressVerificationStore } from "services/addressVerification/addressVerificationStore";
import { type IssuesStore } from "services/issues/issuesStore";
import { IdentityQuestion } from "services/questions/gb/identityConstants";
import { QuestionsStore } from "services/questions/questionsStore";
import { type SumsubApplicationStore } from "services/sumsub/sumsubApplicationStore";

export type IdentityQuestionsState = "default" | "error" | "addressNotVerified";

export class IdentityQuestionsStore extends QuestionsStore<IdentityQuestion> {
  private _state: IdentityQuestionsState = "default";

  constructor(
    private readonly _addressVerificationStore: AddressVerificationStore,
    private readonly _sumsubStore: SumsubApplicationStore,
    protected readonly _issuesStore: IssuesStore,
  ) {
    super(IdentityQuestion, _issuesStore);

    makeObservable(this, {
      _state: observable,
    } as any);
  }

  get state(): IdentityQuestionsState {
    return this._state;
  }

  set state(value: IdentityQuestionsState) {
    this._state = value;
  }

  onOpenbankingCheckError(): void {
    this.state = "error";
    this.goToQuestion(IdentityQuestion.Poa);
  }

  onAddressNotVerified(): void {
    this.state = "addressNotVerified";
    this.goToQuestion(IdentityQuestion.Poa);
  }

  resetState(): void {
    this.state = "default";
  }

  protected _shouldSkipQuestion(question: IdentityQuestion): boolean {
    switch (question) {
      case IdentityQuestion.IdentityIntro:
        return false;
      case IdentityQuestion.NameExtension:
        return !this._sumsubStore.needsNameExtension;
      case IdentityQuestion.Poa:
      case IdentityQuestion.PoaOpenbanking:
        return !this._addressVerificationStore.isPoaRequired;
      case IdentityQuestion.PoaAddressMismatch:
        return true;
    }

    return super._shouldSkipQuestion(question);
  }

  protected _getPreviousQuestionKey(): IdentityQuestion | undefined {
    const prevQuestionKey = super._getPreviousQuestionKey();

    switch (prevQuestionKey) {
      case IdentityQuestion.IdentityCompliance:
      case IdentityQuestion.NameExtension:
        return IdentityQuestion.Identity;
      case IdentityQuestion.IdentityIntro:
        return undefined;
    }

    return prevQuestionKey;
  }

  protected _getRelevantQuestion(): IdentityQuestion | undefined {
    if (this._sumsubStore.isPassedVerifications) {
      if (this._sumsubStore.needsNameExtension) {
        return IdentityQuestion.NameExtension;
      }
      return IdentityQuestion.IdentityCompliance;
    }

    return IdentityQuestion.IdentityIntro;
  }
}
